// Growth › an inquiry's Reply: "Reply" for the inquiry page's header, and the
// inquiry's conversation (their website message, then every email both ways). Reply opens the one-off compose
// (pages/signups/OutreachCompose.tsx, the same panel a signup's profile, a
// tenant's Email and a lead's Email use) in a dialog, a bottom sheet on a phone,
// to the address that wrote to us, with their message quoted under it.
//
//   <InquiryReplyButton inquiry={inquiry} />     in the header
//   <InquiryConversation inquiry={inquiry} />     on the page
//
// A reply is 1:1, not marketing: no footer, no postal address, no unsubscribe
// link. It starts with the server's subject ("Re: " and their topic) and a blank
// body; a template picked fills only the body. It goes now (no Schedule). A reply
// recorded, sent or a dry run, moves a new inquiry to answered. The owner's
// confirm in the compose is the approval; nothing here sends on its own. Their
// message is a stranger's words: rendered as text, never as markup.

import { useState } from 'react'
import { api } from '@/api'
import { MERGE_FIELDS } from '@/api/growthCampaignTypes'
import type { InquiryOutreach } from '@/api/growthSignups'
import type { Inquiry } from '@/api/types'
import { Button, Dialog, ErrorState, SkeletonRows } from '@/components/ui'
import { formatDateTime } from '@/lib/format'
import { invalidateQueries, useQuery } from '@/lib/query'
import { OutreachCompose, type OutreachTarget } from '@/pages/signups/OutreachCompose'
import type { ConversationMessage } from '@/api'
import { reSubject, type ReplyTo } from '@/lib/conversation'
import { ConversationCard } from './LeadConversation'

const FIELDS_HINT =
  `Merge fields: ${MERGE_FIELDS.map((f) => `{{${f}}}`).join(', ')}. What they sent fills first_name and contact_name (their name), league_name (their organisation, or the league they named) and sport, when they gave them; the rest have no value here. ` +
  'A fallback fills a missing value: {{first_name|there}}. The signature goes under it. No footer: a reply isn\'t marketing.'

const outreachKey = (id: number) => ['growth', 'inquiry-outreach', id] as const

function replyTarget(inquiry: Inquiry, data: InquiryOutreach, reply: ReplyTo | null = null): OutreachTarget {
  const withReply = <T extends object>(draft: T) => (reply ? { ...draft, reply_to: reply.message_id } : draft)
  return {
    to: data.to,
    suppressed: data.suppressed,
    noAddress: "This inquiry's address isn't one we can email.",
    previewKey: ['growth', 'inquiry-outreach-preview', inquiry.id],
    preview: (draft, signal) => api.growth.outreach.previewInquiry(inquiry.id, withReply(draft), signal),
    send: (draft) => api.growth.outreach.sendInquiry(inquiry.id, withReply(draft)),
    onSent: () => invalidateQueries('growth'),
    fieldsHint: FIELDS_HINT,
    start: { subject: reply ? reSubject(reply.subject) : data.subject, body: '' },
  }
}

/** "Reply": opens the compose for this inquiry in a dialog. */
export function InquiryReplyButton({ inquiry }: { inquiry: Inquiry }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button variant="primary" onClick={() => setOpen(true)}>
        Reply
      </Button>
      <InquiryReplyDialog inquiry={inquiry} open={open} onClose={() => setOpen(false)} />
    </>
  )
}

/** The dialog itself: the compose, then their message as they wrote it. */
export function InquiryReplyDialog({ inquiry, open, onClose, reply = null }: { inquiry: Inquiry; open: boolean; onClose: () => void; reply?: ReplyTo | null }) {
  const data = useQuery(open ? outreachKey(inquiry.id) : null, (signal) => api.growth.outreach.inquiry(inquiry.id, signal))
  const options = useQuery(open ? ['signups', 'outreach-options'] : null, (signal) => api.growth.outreach.options(signal))
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={`Reply to ${inquiry.name ?? inquiry.email}`}
      description="A 1:1 email from ScoreLM <contact@scorelm.com>, signed by one of the team. It answers them, so it isn't marketing: no footer, no unsubscribe link. Their answer comes back to that mailbox."
    >
      <div className="grid gap-5">
        {data.status === 'error' && !data.data ? (
          <ErrorState error={data.error} title="Couldn't load this inquiry's reply" onRetry={data.refetch} />
        ) : options.status === 'error' && !options.data ? (
          <ErrorState error={options.error} title="Couldn't load the compose panel" onRetry={options.refetch} />
        ) : !data.data || !options.data ? (
          <SkeletonRows rows={4} label="Loading the compose panel" />
        ) : (
          <OutreachCompose
            key={reply?.message_id ?? 'new'}
            bare
            target={replyTarget(inquiry, data.data, reply)}
            options={options.data}
            recipient={
              data.data.to ? (
                <p className="break-all text-[13px] text-ink" data-testid="reply-to">
                  <span className="text-muted">To </span>
                  {data.data.to}
                </p>
              ) : null
            }
          />
        )}
        <section aria-label="Their message" className="grid gap-1.5">
          <h3 className="text-sm font-bold text-ink">Their message</h3>
          <p className="text-xs text-muted">
            {inquiry.name ? `${inquiry.name}, ` : ''}
            {formatDateTime(inquiry.created_at)}
          </p>
          {inquiry.message ? (
            <blockquote data-testid="reply-their-message" className="whitespace-pre-wrap break-words border-l-2 border-line pl-3 text-[13px] leading-relaxed text-muted">
              {inquiry.message}
            </blockquote>
          ) : (
            <p className="text-[13px] text-muted">They sent no message.</p>
          )}
        </section>
      </div>
    </Dialog>
  )
}

/**
 * The inquiry's conversation: their website message first, then every email
 * with them both ways (our replies, their answers, mail in Gmail), oldest first.
 */
export function InquiryConversation({ inquiry }: { inquiry: Inquiry }) {
  const first: ConversationMessage | null = inquiry.message
    ? {
        key: `inquiry:${inquiry.id}`, source: 'snippet', direction: 'in', status: 'received', at: inquiry.created_at,
        from: inquiry.name ? `${inquiry.name} <${inquiry.email}>` : inquiry.email, to: 'ScoreLM', subject: inquiry.topic ? `Website: ${inquiry.topic}` : 'Website message',
        text: inquiry.message, message_id: null, campaign: null, signer_name: null, replied: false, bounced: false, unsubscribed: false, from_gmail: false,
      }
    : null
  return (
    <ConversationCard
      who={inquiry.name ?? inquiry.email}
      email={inquiry.email}
      queryKey={['growth', 'inquiry-conversation', inquiry.id]}
      load={(signal) => api.growth.inquiries.conversation(inquiry.id, signal)}
      canEmail
      headerActions={false}
      first={first}
      compose={(reply, close) => <InquiryReplyDialog inquiry={inquiry} open reply={reply} onClose={close} />}
    />
  )
}
